import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { trackOabCaptureTriggerArmed } from 'app/services/tracking/oabCapture/oabCaptureEvents';

export type OabCaptureTriggerEvent = 'first_calculation' | 'first_query';

type OabCaptureTriggerValue = {
  armedTrigger: OabCaptureTriggerEvent | null;
  armOabCapture: (trigger: OabCaptureTriggerEvent) => void;
};

const noopTrigger: OabCaptureTriggerValue = {
  armedTrigger: null,
  armOabCapture: () => undefined,
};

const OabCaptureTriggerContext = createContext<OabCaptureTriggerValue>(
  noopTrigger,
);

export const OabCaptureTriggerProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement => {
  const [
    armedTrigger,
    setArmedTrigger,
  ] = useState<OabCaptureTriggerEvent | null>(null);

  const armOabCapture = useCallback((trigger: OabCaptureTriggerEvent): void => {
    setArmedTrigger((alreadyArmed) => alreadyArmed ?? trigger);
  }, []);

  // O denominador da taxa de exibição: dispara uma única vez, na transição
  // de nenhum gatilho armado para o primeiro — armedTrigger nunca volta a
  // null nesta sessão do provider.
  useEffect(() => {
    if (armedTrigger !== null) {
      trackOabCaptureTriggerArmed({ triggerEvent: armedTrigger });
    }
  }, [armedTrigger]);

  const value = useMemo(() => ({ armedTrigger, armOabCapture }), [
    armedTrigger,
    armOabCapture,
  ]);

  return (
    <OabCaptureTriggerContext.Provider value={value}>
      {children}
    </OabCaptureTriggerContext.Provider>
  );
};

export const useOabCaptureTrigger = (): OabCaptureTriggerValue =>
  useContext(OabCaptureTriggerContext);
